import React, { FC } from "react";
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  IconButton,
  Button,
  Tooltip,
  Skeleton
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Edit as EditIcon,
  HighlightOff as DeleteIcon,
  AddCircle as AddIcon,
} from "@mui/icons-material";
import AddCostsModal from "../modals/AddCosts/AddCostsModal";
import { AddCostObj } from "../../../GlobalTypes";
import { styled } from "@mui/material/styles";
import useAddCostsSelector from "./useAddCostsSelector";

const StyledCol = styled("div")({
  dislay: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: 10,
  paddingBottom: 20,
});

const StyledRow = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const StyledAccordion = styled(Accordion)({
  width: "100%",
  boxShadow: "none",
  border: 0,
});

const StyledExpandMoreIcon = styled(ExpandMore)({
  position: "absolute",
  top: 0,
  left: 130,
});

const StyledExpandLessIcon = styled(ExpandLess)({
  position: "absolute",
  top: 0,
  left: 130,
});

function displayPrice(price: number) {
  if (!price) return;

  if (String(price).includes(".")) {
    const dec = String(price).split(".")[1];
    if (dec && dec.length === 1) {
      return `${price}0`;
    }
  }

  return price;
}

interface AddCostsProps {
  costsArray: AddCostObj[];
  quoteId: string;
  disableChanges: boolean;
  margin: string;
  withMarkup: boolean;
  hasDealerTax: boolean;
  isFetching: boolean;
}

const AdditionalCosts: FC<AddCostsProps> = ({
  costsArray,
  quoteId,
  disableChanges,
  margin,
  withMarkup,
  hasDealerTax,
  isFetching
}) => {
  const {
    deleteCost,
    handleClose,
    handleOpenNewCost,
    handleOpen,
    handleChange,
    cost,
    open,
    expandAddCost,
  } = useAddCostsSelector(
    quoteId,
    disableChanges,
    margin,
    withMarkup,
    hasDealerTax,
    isFetching
  );

  return (
    <StyledCol>
      <AddCostsModal
        open={open}
        handleClose={handleClose}
        editCost={cost}
        quoteId={quoteId}
        margin={margin}
        withMarkup={withMarkup}
        hasDealerTax={hasDealerTax}
        isFetching={isFetching}
      />

      {isFetching ? (
        <Skeleton animation="wave" variant="rectangular" width="100%" height={50} />
      )
      : costsArray.length ? (
        <StyledAccordion expanded={expandAddCost} onChange={handleChange}>
          <AccordionSummary
            aria-controls="panel1c-content"
            id="panel1c-header"
            style={{ maxHeight: 20, minHeight: 20 }}
          >
            <Typography
              style={{
                position: "relative",
                color: "#3f51b5",
                textDecoration: "underline",
              }}
            >
              Additional Costs{" "}
              {!expandAddCost ? (
                <StyledExpandMoreIcon />
              ) : (
                <StyledExpandLessIcon />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails style={{ width: "100%" }}>
            {costsArray.map((itm, idx) => (
              <StyledRow
                key={idx}
                style={{ justifyContent: "space-between", width: "100%" }}
              >
                {!hasDealerTax ? (
                  <Typography variant="subtitle2">
                    -{" "}
                    {`${itm.title || "Additional Cost"} ${
                      itm.taxable ? "(Tax)" : "(No-Tax)"
                    }`}
                  </Typography>
                ) : (
                  <Typography variant="subtitle2">
                    - {`${itm.title || "Additional Cost"}`}
                  </Typography>
                )}

                <StyledRow>
                  <Typography variant="subtitle2" style={{ paddingRight: 10 }}>
                    ${displayPrice(itm.price)}
                  </Typography>

                  {!disableChanges && (
                    <>
                      <Tooltip placement="top" title="Delete">
                        <IconButton
                          aria-label="delete"
                          onClick={() =>
                            deleteCost(itm._id || null, itm.taxable)
                          }
                        >
                          <DeleteIcon style={{ color: "crimson" }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip placement="top" title="Edit">
                        <IconButton
                          aria-label="edit"
                          onClick={() => handleOpen(itm)}
                        >
                          <EditIcon color={"cyan" as "primary"} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </StyledRow>
              </StyledRow>
            ))}
            {costsArray.length < 3 && !disableChanges ? (
              <StyledRow
                style={{ justifyContent: "space-between", width: "100%" }}
              >
                <AddNewCost handleOpen={handleOpenNewCost} />
              </StyledRow>
            ) : null}
          </AccordionDetails>
        </StyledAccordion>
      ) : !disableChanges ? (
        <AddNewCost handleOpen={handleOpenNewCost} />
      ) : null}
    </StyledCol>
  );
};

interface AddNewCostProps {
  handleOpen: () => void;
}

const AddNewCost: FC<AddNewCostProps> = ({ handleOpen }) => {
  return (
    <Button
      onClick={handleOpen}
      variant="outlined"
      startIcon={<AddIcon />}
      style={{ border: "none" }}
      size="small"
    >
      Add Cost
    </Button>
  );
};

export default AdditionalCosts;
